
import * as React from "react"
import { isMobileDevice, isIOSDevice } from "@/utils/environment";

const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024

// Reports if screen width is mobile-sized (may be desktop browser resized small)
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const checkMobile = () => {
      const byWidth = window.innerWidth < MOBILE_BREAKPOINT;
      console.log(`useIsMobile() - Width-based mobile detection:
        - Window width: ${window.innerWidth}px
        - Breakpoint: ${MOBILE_BREAKPOINT}px
        - Result: ${byWidth ? 'Mobile (by width)' : 'Desktop (by width)'}
      `);
      return byWidth;
    };
    
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(checkMobile());
    }
    
    mql.addEventListener("change", onChange)
    setIsMobile(checkMobile())
    
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isMobile
}

// Reports if the actual device is mobile based on user agent (not just screen size)
export function useIsTrueMobile() {
  const [isTrueMobile, setIsTrueMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const result = isMobileDevice();
    console.log(`useIsTrueMobile() - Device-based mobile detection:
      - Result: ${result ? 'Mobile device (by UA)' : 'Desktop device (by UA)'}
      - iOS device: ${isIOSDevice() ? 'Yes' : 'No'}
    `);
    setIsTrueMobile(result);
  }, []);

  return !!isTrueMobile;
}

// Combine both methods to get the most accurate result based on context
export function useDeviceContext() {
  const isMobileWidth = useIsMobile();
  const isMobileDevice = useIsTrueMobile();
  const isIOSOnly = React.useMemo(() => isIOSDevice(), []);
  
  return {
    isMobileWidth,       // True if screen width is mobile-sized (may be desktop browser resized small)
    isMobileDevice,      // True if actual device is mobile based on UA (more reliable for device capabilities)
    isIOSDevice: isIOSOnly, // True specifically for iOS devices which need special handling
    
    // Convenience values for common use cases:
    requiresMobileTreatment: isMobileDevice || isIOSOnly, // Should we treat this as a mobile device (for video playback etc)
    usesMobileUI: isMobileWidth,            // Should we show the mobile UI layout
  };
}

export function useIsTablet() {
  const [isTablet, setIsTablet] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px) and (max-width: ${TABLET_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsTablet(window.innerWidth >= MOBILE_BREAKPOINT && window.innerWidth < TABLET_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsTablet(window.innerWidth >= MOBILE_BREAKPOINT && window.innerWidth < TABLET_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isTablet
}

export function useIsTabletOrMobile() {
  const [isTabletOrMobile, setIsTabletOrMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${TABLET_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsTabletOrMobile(window.innerWidth < TABLET_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsTabletOrMobile(window.innerWidth < TABLET_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isTabletOrMobile
}
